import React, { useRef, useState, useEffect } from 'react';

const SignatureField = ({ fieldId, value, onChange }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const contextRef = useRef(null);
  const currentStrokeRef = useRef([]);
  const allStrokesRef = useRef([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d', { 
      willReadFrequently: false, 
      desynchronized: true,
      alpha: false 
    });
    contextRef.current = ctx;

    const initCanvas = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      ctx.scale(dpr, dpr);
      
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, rect.width, rect.height);
      
      ctx.strokeStyle = '#000000';
      ctx.fillStyle = '#000000';
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      
      if (value && typeof value === 'string') {
        const img = new Image();
        img.onload = () => {
          ctx.drawImage(img, 0, 0, rect.width, rect.height);
          setHasSignature(true);
        };
        img.onerror = () => setHasSignature(false);
        img.src = value;
      }
    };

    initCanvas();
    
    const handleTouchMove = (e) => {
      if (isDrawing) e.preventDefault();
    };
    
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('resize', initCanvas);
    
    return () => {
      canvas.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('resize', initCanvas);
    };
  }, [value]);

  const getPoint = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches?.[0];
    
    return {
      x: (touch?.clientX || e.clientX) - rect.left,
      y: (touch?.clientY || e.clientY) - rect.top,
      time: performance.now()
    };
  };

  const distance = (p1, p2) => {
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const simplifyPoints = (points, tolerance = 1.0) => {
    if (points.length < 3) return points;
    
    const simplified = [points[0]];
    
    for (let i = 1; i < points.length - 1; i++) {
      const prev = simplified[simplified.length - 1];
      const curr = points[i];
      const dist = distance(prev, curr);
      
      if (dist >= tolerance) {
        simplified.push(curr);
      }
    }
    
    simplified.push(points[points.length - 1]);
    return simplified;
  };

  const drawAllStrokes = () => {
    const ctx = contextRef.current;
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, rect.width, rect.height);
    
    allStrokesRef.current.forEach(stroke => {
      drawStroke(stroke, false);
    });
    
    if (currentStrokeRef.current.length > 0) {
      drawStroke(currentStrokeRef.current, false);
    }
  };

  const drawStroke = (points) => {
    if (points.length < 1) return;
    
    const ctx = contextRef.current;
    const simplified = simplifyPoints(points, 1.0);
    
    ctx.strokeStyle = '#000000';
    ctx.fillStyle = '#000000';
    ctx.lineWidth = 1.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    
    if (simplified.length === 1) {
      ctx.beginPath();
      ctx.arc(simplified[0].x, simplified[0].y, 0.6, 0, 2 * Math.PI);
      ctx.fill();
      return;
    }
    
    ctx.beginPath();
    ctx.moveTo(simplified[0].x, simplified[0].y);
    
    for (let i = 1; i < simplified.length - 2; i++) {
      const xc = (simplified[i].x + simplified[i + 1].x) / 2;
      const yc = (simplified[i].y + simplified[i + 1].y) / 2;
      ctx.quadraticCurveTo(simplified[i].x, simplified[i].y, xc, yc);
    }
    
    if (simplified.length > 2) {
      ctx.quadraticCurveTo(
        simplified[simplified.length - 2].x,
        simplified[simplified.length - 2].y,
        simplified[simplified.length - 1].x,
        simplified[simplified.length - 1].y
      );
    } else {
      ctx.lineTo(simplified[1].x, simplified[1].y);
    }
    
    ctx.stroke();
  };

  const startDraw = (e) => {
    e.preventDefault();
    const point = getPoint(e);
    
    currentStrokeRef.current = [point];
    setIsDrawing(true);
    setHasSignature(true);
    
    const ctx = contextRef.current;
    ctx.beginPath();
    ctx.arc(point.x, point.y, 0.6, 0, 2 * Math.PI);
    ctx.fill();
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    
    const point = getPoint(e);
    const lastPoint = currentStrokeRef.current[currentStrokeRef.current.length - 1];
    
    if (lastPoint && distance(lastPoint, point) < 0.5) return;
    
    currentStrokeRef.current.push(point);
    
    drawAllStrokes();
  };

  const endDraw = (e) => {
    if (!isDrawing) return;
    e?.preventDefault();
    
    if (currentStrokeRef.current.length > 0) {
      allStrokesRef.current.push([...currentStrokeRef.current]);
      currentStrokeRef.current = [];
    }
    
    setIsDrawing(false);
    
    requestAnimationFrame(() => {
      if (canvasRef.current) {
        const dataURL = canvasRef.current.toDataURL('image/png', 1.0);
        onChange(dataURL);
      }
    });
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = contextRef.current;
    const rect = canvas.getBoundingClientRect();
    
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, rect.width, rect.height);
    
    currentStrokeRef.current = [];
    allStrokesRef.current = [];
    setHasSignature(false);
    onChange(null);
  };

  return (
    <div className="flex flex-col gap-3 w-full">
      <style>
        {`
          .signature-canvas {
            cursor: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none'%3E%3Cpath d='M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z' fill='%232563eb'/%3E%3Ccircle cx='3' cy='21' r='1.5' fill='%233b82f6'/%3E%3C/svg%3E") 3 21, crosshair !important;
          }
        `}
      </style>
      
      <div className="relative w-full h-60 bg-white rounded-lg border border-gray-300 overflow-hidden shadow-sm">
        <canvas 
          ref={canvasRef} 
          id={fieldId} 
          onMouseDown={startDraw} 
          onMouseMove={draw} 
          onMouseUp={endDraw} 
          onMouseLeave={endDraw} 
          onTouchStart={startDraw} 
          onTouchMove={draw} 
          onTouchEnd={endDraw} 
          onTouchCancel={endDraw} 
          className="w-full h-full signature-canvas" 
          style={{ touchAction: 'none' }} 
        />
        
        {!hasSignature && !value && (
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <svg className="w-10 h-10 text-gray-300 mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
            <span className="text-xs text-gray-400">Sign Here</span>
          </div>
        )}
      </div>
      
      <button 
        type="button" 
        onClick={clearCanvas} 
        className="self-end px-4 py-1.5 text-xs font-medium text-red-600 bg-white border border-red-200 rounded hover:bg-red-50 active:scale-95 transition-all"
      >
        Clear
      </button>
    </div>
  );
};

export default SignatureField;